<template>
  <div class="question-mobile-wrapper">
    <div class="question-mobile-container">
      <!-- 操作按钮区域，固定在右下角 -->
      <div class="mobile-action-buttons">
        <!-- 查询按钮 -->
        <el-button class="mobile-icon-btn" @click="showFilterDialog">
          <i class="el-icon-search"></i>
        </el-button>
        
        <!-- 统计按钮 -->
        <el-button class="mobile-icon-btn" @click="showStatisticsDialog">
          <i class="el-icon-data-analysis"></i>
        </el-button>
      </div>
      
      <!-- 标题栏 -->
      <div class="mobile-header" :class="{ 'header-hidden': !headerVisible }">
        <h1 class="page-title">题目列表</h1>
      </div>
      
      <!-- 题目卡片区域 -->
      <div class="question-cards-container" :class="{ 'header-hidden': !headerVisible }" v-loading="loading" @scroll="handleScroll">
        <div v-if="questionList.length === 0 && !loading" class="no-data">
          <div class="no-data-content">
            <i class="el-icon-document"></i>
            <span class="no-data-title">暂无题目</span>
            <p class="no-data-tips">
              没有找到符合条件的题目，请尝试调整筛选条件
            </p>
          </div>
        </div>
        
        <template v-else>
          <!-- 题目卡片 -->
          <div 
            v-for="question in questionList" 
            :key="question.question_code"
            class="question-card"
            @click="handleCardClick(question)"
            @touchstart="onTouchStart($event, question)"
            @touchmove="onTouchMove($event, question)"
            @touchend="onTouchEnd($event, question)"
          >
            <!-- 难度指示器（右上角三角形） -->
            <div class="difficulty-indicator">
              <div class="triangle-background"
                   :style="{ 
                     'border-color': question.question_level ? 
                       `transparent ${getDifficultyColor(question.question_level, 0.85)} transparent transparent` : 
                       'transparent rgba(144, 147, 153, 0.85) transparent transparent'
                   }">
              </div>
              <span class="difficulty-value">
                {{ question.question_level || '未定义' }}
              </span>
            </div>
            
            <!-- 滑动显示操作按钮 -->
            <div class="swipe-actions" :class="{ 'show': question._showSwipeActions }">
              <div class="action-buttons">
                <el-button class="action-icon-btn" :disabled="question.question_score_fix === 100 || actionLoading" @click.stop="handleQuestionFix(question)" title="订正">
                  <i class="el-icon-check"></i>
                </el-button>
                <el-button class="action-icon-btn" :disabled="actionLoading" @click.stop="handleQuestionCollect(question)" :title="question.is_collect ? '取消收藏' : '收藏'">
                  <i :class="{'el-icon-star-off': !question.is_collect, 'el-icon-star-on': question.is_collect}"></i>
                </el-button>
                <el-button class="action-icon-btn" :disabled="actionLoading" @click.stop="handleQuestionPending(question)" :title="question.is_pending ? '取消挂起' : '挂起'">
                  <i class="el-icon-video-pause" :style="{ color: question.is_pending ? '#E6A23C' : '#409EFF' }"></i>
                </el-button>
                <el-button class="action-icon-btn" :disabled="!question.question_url || actionLoading" @click.stop="handleQuestionUrl(question)" title="打开链接">
                  <i class="el-icon-link"></i>
                </el-button>
              </div>
            </div>
            
            <!-- AC/订正水印 -->
            <div v-if="question.question_score === 100 || question.question_score_fix === 100" class="fix-stamp">
              <div class="stamp-content" :class="{ 'ac-stamp': question.question_score === 100, 'fix-stamp-content': question.question_score !== 100 && question.question_score_fix === 100 }">
                {{ question.question_score === 100 ? 'AC' : '订' }}
              </div>
            </div>
            
            <!-- 题目基本信息 -->
            <div class="question-card-main">
              <!-- 题目名称区域 -->
              <div class="question-header">
                <!-- 录像机图标 - 表示有复盘内容 - 移到题目名称左侧 -->
                <div v-if="question.has_replay" class="replay-icon">
                  <i class="el-icon-video-camera"></i>
                </div>
                <h3 class="question-name">{{ question.question_name }}</h3>
              </div>
              
              <div class="question-info">
                <!-- 赛事名称 -->
                <div class="question-game"><i class="el-icon-trophy"></i><span class="game-name-text">{{ question.game_name }}</span></div>
                
                <!-- 分数区域 - 修改为并排式布局 -->
                <div class="question-scores-row">
                  <!-- 得分 -->
                  <div class="question-score">
                    <span class="score-label">得分：</span>
                    <div class="score-value-container">
                      <span class="score-value" :style="{color: getScoreColor(question.question_score)}">
                        {{ question.question_score }}
                      </span>
                    </div>
                  </div>
                  
                  <!-- 订正分数 -->
                  <div class="question-fix-score">
                    <span class="score-label">订正：</span>
                    <div class="score-value-container">
                      <span class="score-value" :style="{color: getScoreColor(question.question_score_fix)}">
                        {{ question.question_score_fix }}
                      </span>
                    </div>
                  </div>
                </div>
                
                <!-- 错误类型（放在分数行下、标签上方） -->
                <div v-if="normalizeErrors(question.errors).length > 0" class="question-errors">
                  <el-tag
                    v-for="err in (question._showAllErrors ? normalizeErrors(question.errors) : normalizeErrors(question.errors).slice(0, 2))"
                    :key="(err.error_code || err.error_name) + '-m'"
                    :style="getErrorOutlineStyle(err.error_code || err.error_name)"
                    size="mini"
                    style="margin-right: 4px; margin-bottom: 4px;">
                    <i class="el-icon-warning" style="margin-right: 3px;"></i>{{ err.error_name || err }}
                  </el-tag>
                  <el-button 
                    v-if="normalizeErrors(question.errors).length > 2" 
                    type="text" 
                    size="mini" 
                    class="more-tags-btn"
                    @click.stop="toggleErrorsDisplay(question)">
                    {{ question._showAllErrors ? '收起' : `更多(${normalizeErrors(question.errors).length - 2})` }}
                  </el-button>
                </div>

                <!-- 标签 -->
                <div v-if="question.tags && question.tags.length > 0" class="question-tags">
                  <el-tag
                    v-for="(tagName, index) in question.tags.slice(0, question._showAllTags ? question.tags.length : 3)"
                    :key="tagName"
                    :style="{ 
                      backgroundColor: getTagColorByName(tagName),
                      borderColor: getTagColorByName(tagName),
                      color: 'white',
                      marginRight: '4px',
                      marginBottom: '4px'
                    }"
                    size="mini">
                    {{ tagName }}
                  </el-tag>
                  <el-button 
                    v-if="question.tags.length > 3" 
                    type="text" 
                    size="mini" 
                    class="more-tags-btn"
                    @click.stop="toggleTagsDisplay(question)">
                    {{ question._showAllTags ? '收起' : `更多(${question.tags.length - 3})` }}
                  </el-button>
                </div>
              </div>
            </div>
          </div>
          
          <!-- 加载更多 -->
          <div v-if="hasMore && !loading && questionList.length > 0" class="load-more">
            <el-button type="text" @click="loadMore">加载更多</el-button>
          </div>
          <div v-if="loading && questionList.length > 0" class="loading-more">
            <i class="el-icon-loading"></i>
            <span>加载中...</span>
          </div>
          <div v-if="!hasMore && questionList.length > 0" class="no-more">
            <span>------已经到底啦------</span>
            <div class="copyright-info">
              <span class="mobile-copyright">Copyright © 2024 OIer之路 All rights reserved.<br>闽ICP备2025095302号-1</span>
            </div>
          </div>
        </template>
      </div>
    </div>
    
    <!-- 自定义统计信息弹窗 -->
    <div class="dialog-container" v-show="statisticsDialogVisible">
      <div class="dialog-mask" @click="statisticsDialogVisible = false"></div>
      <div class="custom-dialog stats-dialog" style="top: 10vh;">
        <div class="dialog-close" @click="statisticsDialogVisible = false">×</div>
        <div class="dialog-body statistics-dialog-body">
          <div class="dialog-content" style="background-color: #ffffff;">
            <h3 class="dialog-custom-title">题目统计</h3>
            <div class="mobile-statistics-grid">
              <!-- 总题数 -->
              <div class="stat-item">
                <div class="stat-value">{{ formatNumber(statistics.total) }}</div>
                <div class="stat-label">
                  <i class="el-icon-notebook-1"></i>
                  总题数
                </div>
              </div>
              
              <!-- 平均分 -->
              <div class="stat-item">
                <div class="stat-value">{{ formatNumber(statistics.averageScore) }}</div>
                <div class="stat-label">
                  <i class="el-icon-data-line"></i>
                  平均分
                </div>
              </div>
              
              <!-- AC数 -->
              <div class="stat-item">
                <div class="stat-value">{{ formatNumber(statistics.acCount || 0) }}</div>
                <div class="stat-label">
                  <i class="el-icon-circle-check"></i>
                  AC数
                </div>
              </div>
              
              <!-- 订正数 -->
              <div class="stat-item">
                <div class="stat-value">{{ formatNumber(statistics.fixCount || 0) }}</div>
                <div class="stat-label">
                  <i class="el-icon-notebook-2"></i>
                  订正数
                </div>
              </div>
              
              <!-- 未订正 -->
              <div class="stat-item">
                <div class="stat-value unfix-value">{{ formatNumber(statistics.unfixCount || 0) }}</div>
                <div class="stat-label">
                  <i class="el-icon-bell"></i>
                  未订正
                </div>
              </div>
              
              <!-- 挂起 -->
              <div class="stat-item">
                <div class="stat-value pending-value">{{ formatNumber(statistics.pendingCount || 0) }}</div>
                <div class="stat-label">
                  <i class="el-icon-video-pause"></i>
                  挂起
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 自定义查询筛选弹窗 -->
    <div class="dialog-container" v-show="filterDialogVisible">
      <div class="dialog-mask" @click="filterDialogVisible = false"></div>
      <div class="custom-dialog" style="top: 10vh;">
        <div class="dialog-close" @click="filterDialogVisible = false">×</div>
        <div class="dialog-body filter-dialog-body">
          <div class="dialog-content filter-dialog-content" style="background-color: #ffffff;">
            <h3 class="dialog-custom-title filter-title">查询条件</h3>
            <el-form :model="filterForm" class="filter-form" size="small" label-width="70px">
              <el-form-item label="题目名称">
                <el-input v-model="filterForm.question_name" placeholder="请输入题目名称"></el-input>
              </el-form-item>
              
              <el-form-item label="赛事名称">
                <el-input v-model="filterForm.game_name" placeholder="请输入赛事名称"></el-input>
              </el-form-item>
              
              <el-form-item label="订正状态">
                <el-select v-model="filterForm.fix_status" placeholder="请选择订正状态" clearable style="width: 100%">
                  <el-option label="已完成" value="已完成"></el-option>
                  <el-option label="未完成" value="未完成"></el-option>
                </el-select>
              </el-form-item>
              
              <el-form-item label="挂起状态">
                <el-select v-model="filterForm.is_pending" placeholder="请选择挂起状态" clearable style="width: 100%">
                  <el-option label="是" value="是"></el-option>
                  <el-option label="否" value="否"></el-option>
                </el-select>
              </el-form-item>
              
              <el-form-item label="题目难度">
                <el-select v-model="filterForm.difficulty" placeholder="请选择题目难度" clearable style="width: 100%">
                  <el-option label="未定义" value="未定义"></el-option>
                  <el-option label="红题" value="红题"></el-option>
                  <el-option label="橙题" value="橙题"></el-option>
                  <el-option label="黄题" value="黄题"></el-option>
                  <el-option label="绿题" value="绿题"></el-option>
                  <el-option label="蓝题" value="蓝题"></el-option>
                  <el-option label="紫题" value="紫题"></el-option>
                  <el-option label="黑题" value="黑题"></el-option>
                </el-select>
              </el-form-item>

              <el-form-item label="错误类型">
                <el-select
                  v-model="filterForm.error_code"
                  filterable
                  clearable
                  placeholder="请选择错误类型"
                  style="width: 100%">
                  <el-option 
                    v-for="err in errorCodes" 
                    :key="err.error_code" 
                    :label="err.error_name" 
                    :value="err.error_code">
                    <el-tooltip :content="err.error_note" placement="right" effect="dark">
                      <span>
                        <el-tag :style="getErrorOutlineStyle(err.error_code)" size="mini" style="margin-right: 6px;">
                          <i class="el-icon-warning" style="margin-right: 3px;"></i>{{ err.error_name }}
                        </el-tag>
                      </span>
                    </el-tooltip>
                  </el-option>
                </el-select>
              </el-form-item>
              
              <el-form-item label="标签">
                <el-select
                  v-model="filterForm.tags"
                  multiple
                  filterable
                  placeholder="请选择标签"
                  style="width: 100%">
                  <el-option
                    v-for="tag in sortedTags"
                    :key="tag.tag_code"
                    :label="tag.tag_name"
                    :value="tag.tag_name">
                    <el-tag
                      :style="{ 
                        backgroundColor: getTagColorByCode(tag.tag_code),
                        borderColor: getTagColorByCode(tag.tag_code),
                        color: 'white'
                      }"
                      style="margin-right: 4px">
                      {{ tag.tag_name }}
                    </el-tag>
                  </el-option>
                </el-select>
              </el-form-item>
              
              <el-form-item label="收藏状态">
                <el-select v-model="filterForm.is_collect" placeholder="请选择收藏状态" clearable style="width: 100%">
                  <el-option label="是" value="是"></el-option>
                  <el-option label="否" value="否"></el-option>
                </el-select>
              </el-form-item>
            </el-form>
            <div class="dialog-footer filter-footer">
              <el-button size="small" @click="handleReset">重置</el-button>
              <el-button size="small" type="primary" @click="handleSearch">查询</el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 复盘对话框 -->
    <div class="dialog-container" v-show="replayDialogVisible">
      <div class="dialog-mask" @click="replayDialogVisible = false"></div>
      <div class="custom-dialog" style="top: 10vh;">
        <div class="dialog-close" @click="replayDialogVisible = false">×</div>
        <div class="dialog-body replay-dialog-body">
          <div class="dialog-content replay-dialog-content" style="background-color: #ffffff;">
            <h3 class="dialog-custom-title">题目复盘</h3>
            <div v-if="currentReplayInfo" class="replay-content">
              <mavon-editor 
                :value="currentReplayInfo"
                :subfield="false"
                :defaultOpen="'preview'"
                :toolbarsFlag="false"
                :editable="false"
                :boxShadow="false"
                :navigation="false"
                previewBackground="#fff"
                class="md-editor"
              />
            </div>
            <div v-else class="empty-text">暂无复盘信息</div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
// 导入所需模块
import { getQuestionList, getQuestionReplay } from '@/api/question'
import { getAllTags } from '@/api/tag'
import { getErrorCodes } from '@/api/error'
import mavonEditor from 'mavon-editor'
import 'mavon-editor/dist/css/index.css'
import { getErrorTagInlineStyle } from '@/constants/errorTypes'
import { getErrorOutlineStyle } from '@/constants/errorTypes'

export default {
  name: 'QuestionMobile',
  components: {
    mavonEditor: mavonEditor.mavonEditor
  },
  data() {
    return {
      // 基础数据
      loading: false,
      questionList: [],
      hasMore: true,
      pageNum: 1,
      pageSize: 10,
      
      // 筛选表单
      filterForm: {
        question_name: '',
        game_name: '',
        fix_status: '',
        difficulty: '',
        error_code: '',
        error_types: [],
        tags: [],
        is_collect: '',
        is_pending: ''
      },
      
      // 弹窗控制
      filterDialogVisible: false,
      statisticsDialogVisible: false,
      
      // 其他数据
      tags: [],
      errorCodes: [],
      statistics: {
        total: 0,
        averageScore: 0,
        acCount: 0,
        fixCount: 0,
        unfixCount: 0,
        pendingCount: 0
      },
      
      // 复盘相关
      replayDialogVisible: false,
      currentReplayInfo: '',
      currentQuestion: null,
      
      // 触摸滑动状态
      touchStartX: 0,
      touchStartY: 0,
      isTouchMoving: false,
      ignoreEdgeSwipe: false,
      
      // 操作中的loading，避免重复点击
      actionLoading: false,

      // 滚动状态控制
      lastScrollTop: 0,
      headerVisible: true,
      scrollThreshold: 10 // 滚动阈值，避免微小滚动触发
    }
  },
  computed: {
    sortedTags() {
      return [...this.tags].sort((a, b) => a.tag_name.localeCompare(b.tag_name))
    }
  },
  created() {
    this.fetchData()
    this.fetchTags()
    this.fetchStatistics()
    this.fetchErrorCodes()
  },
  methods: {
    // 获取题目列表数据
    async fetchData(isLoadMore = false) {
      if (!isLoadMore) {
        this.loading = true
      }
      
      try {
        // 构建请求参数
        const params = {
          pageNum: this.pageNum,
          pageSize: this.pageSize,
          sort_prop: 'game_time',
          sort_order: 'descending',
          ...this.filterForm
        }
        
        const response = await getQuestionList(params)
        
        if (response && response.code === 200 && response.data) {
          const newItems = response.data.items || []
          
          // 如果是加载更多，则追加数据，否则替换数据
          if (isLoadMore) {
            this.questionList = [...this.questionList, ...newItems]
          } else {
            this.questionList = newItems
          }
          
          // 更新统计信息
          this.statistics.total = response.data.total || 0
          this.statistics.averageScore = response.data.avg_score || 0
          
          // 判断是否还有更多数据
          this.hasMore = this.questionList.length < this.statistics.total
        } else {
          if (!isLoadMore) {
            this.questionList = []
          }
          console.error('返回数据格式不正确:', response)
        }
      } catch (error) {
        console.error('获取题目列表失败:', error)
        this.$message.error('获取题目列表失败')
      } finally {
        this.loading = false
      }
    },
    
    // 获取所有标签
    async fetchTags() {
      try {
        const { data } = await getAllTags()
        this.tags = data
      } catch (error) {
        console.error('获取标签列表失败:', error)
      }
    },
    
    // 获取统计数据
    async fetchStatistics() {
      try {
        // 使用与筛选条件相同的参数获取统计数据
        const params = { ...this.filterForm }
        
        // 这里假设有一个获取题目统计的API，实际可能需要根据项目情况调整
        // 如果没有专门的统计API，可以从题目列表数据中计算
        const response = await getQuestionList({
          ...params,
          pageNum: 1,
          pageSize: 1 // 只需要统计数据，不需要实际题目数据
        })
        
        if (response && response.code === 200 && response.data) {
          // 更新统计信息
          this.statistics.total = response.data.total || 0
          this.statistics.averageScore = response.data.avg_score || 0
          
          // 计算其他统计信息（实际项目中可能需要调整）
          // 这里只是示例，实际应该根据后端返回的数据进行设置
          this.statistics.acCount = response.data.ac_count || 0
          this.statistics.fixCount = response.data.fix_count || 0
          this.statistics.unfixCount = response.data.unfix_count || 0
          this.statistics.pendingCount = response.data.pending_count || 0
        }
      } catch (error) {
        console.error('获取统计数据失败:', error)
      }
    },
    
    // 加载更多数据
    loadMore() {
      if (this.loading || !this.hasMore) return
      
      this.pageNum++
      this.loading = true
      this.fetchData(true)
    },
    
    // 处理滚动事件，实现滚动加载
    handleScroll(e) {
      const container = e.target
      const scrollHeight = container.scrollHeight
      const scrollTop = container.scrollTop
      const clientHeight = container.clientHeight
      
      // 检测滚动方向并控制头部横幅显示
      const deltaY = scrollTop - this.lastScrollTop
      
      if (Math.abs(deltaY) > this.scrollThreshold) {
        if (deltaY > 0) {
          // 向下滚动（内容向上移动），隐藏头部横幅
          this.headerVisible = false
        } else {
          // 向上滚动（内容向下移动），显示头部横幅
          this.headerVisible = true
        }
      }
      
      this.lastScrollTop = scrollTop
      
      // 当滚动到距离底部100px时，加载更多数据（仅在满足条件时）
      if (!this.loading && this.hasMore && scrollHeight - scrollTop - clientHeight < 100) {
        this.loadMore()
      }
      this.hideAllSwipeActions()
    },
    
    // 显示查询弹窗
    showFilterDialog() {
      this.filterDialogVisible = !this.filterDialogVisible
      if (this.filterDialogVisible && (!this.errorCodes || this.errorCodes.length === 0)) {
        this.fetchErrorCodes()
      }
    },
    
    // 显示统计弹窗
    showStatisticsDialog() {
      this.statisticsDialogVisible = !this.statisticsDialogVisible
    },
    
    // 处理查询
    handleSearch() {
      this.pageNum = 1
      this.hasMore = true
      this.filterDialogVisible = false
      this.fetchData()
      this.fetchStatistics()
    },
    
    // 重置筛选条件
    handleReset() {
      this.filterForm = {
        question_name: '',
        game_name: '',
        fix_status: '',
        difficulty: '',
        tags: [],
        is_collect: '',
        is_pending: ''
      }
    },
    
    // 处理卡片点击，显示复盘信息
    async handleCardClick(question) {
      if (question && question._showSwipeActions) {
        this.$set(question, '_showSwipeActions', false)
        return
      }
      
      if (!question.has_replay) {
        this.$message.info('该题目暂无复盘信息')
        return
      }
      
      this.currentQuestion = question
      
      try {
        const { data } = await getQuestionReplay(question.question_code)
        this.currentReplayInfo = data
        this.replayDialogVisible = true
      } catch (error) {
        console.error('获取复盘信息失败:', error)
        this.$message.error('获取复盘信息失败')
      }
    },
    
    // 获取难度颜色
    getDifficultyColor(level, opacity = 1) {
      const colorMap = {
        '红题': `rgba(245, 108, 108, ${opacity})`,  // 红色
        '橙题': `rgba(230, 162, 60, ${opacity})`,   // 橙色
        '黄题': `rgba(247, 186, 42, ${opacity})`,   // 黄色
        '绿题': `rgba(103, 194, 58, ${opacity})`,   // 绿色
        '蓝题': `rgba(64, 158, 255, ${opacity})`,   // 蓝色
        '紫题': `rgba(142, 68, 173, ${opacity})`,   // 紫色
        '黑题': `rgba(44, 62, 80, ${opacity})`,     // 黑色
        '未定义': `rgba(144, 147, 153, ${opacity})` // 灰色
      }
      return colorMap[level] || `rgba(144, 147, 153, ${opacity})`
    },
    
    // 获取标签颜色（通过代码）
    getTagColorByCode(tagCode) {
      // 使用与赛事页面相同的颜色映射
      const colors = {
        'tag-color-1': '#409EFF',  // Element UI 主蓝色
        'tag-color-2': '#67C23A',  // Element UI 成功绿色
        'tag-color-3': '#E6A23C',  // Element UI 警告黄色
        'tag-color-4': '#F56C6C',  // Element UI 危险红色
        'tag-color-5': '#8E44AD',  // 深紫色
        'tag-color-6': '#16A085',  // 深青色
        'tag-color-7': '#D35400',  // 深橙色
        'tag-color-8': '#2980B9',  // 深蓝色
        'tag-color-9': '#27AE60',  // 深绿色
        'tag-color-10': '#C0392B'  // 深红色
      }
      
      const colorClasses = [
        'tag-color-1', 'tag-color-2', 'tag-color-3', 'tag-color-4', 'tag-color-5',
        'tag-color-6', 'tag-color-7', 'tag-color-8', 'tag-color-9', 'tag-color-10'
      ]
      const index = parseInt(tagCode.split('_')[1], 36) % colorClasses.length
      return colors[colorClasses[index]]
    },
    
    // 获取标签颜色（通过名称）
    getTagColorByName(tagName) {
      const tag = this.tags.find(t => t.tag_name === tagName)
      return tag ? this.getTagColorByCode(tag.tag_code) : '#909399'
    },
    
    // 获取分数颜色
    getScoreColor(score) {
      if (score === undefined || score === null) return '#606266'
      const percentage = (score / 100) * 100
      if (percentage >= 100) return '#237804' // 绿色
      if (percentage >= 75) return '#52C41A'  // 黄绿色
      if (percentage >= 50) return '#F7BA2A'  // 黄色
      if (percentage >= 25) return '#E6A23C'  // 橙色
      return '#F56C6C'                        // 红色
    },
    
    // 格式化数字
    formatNumber(num) {
      if (num === undefined || num === null) return '0'
      return num.toString()
    },
    
    // 触摸手势处理
    onTouchStart(e, question) {
      const touch = e.changedTouches[0]
      this.touchStartX = touch.clientX
      this.touchStartY = touch.clientY
      this.isTouchMoving = false
      this.ignoreEdgeSwipe = this.touchStartX <= 25
    },

    onTouchMove(e, question) {
      const touch = e.changedTouches[0]
      const deltaX = touch.clientX - this.touchStartX
      const deltaY = touch.clientY - this.touchStartY
      if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 20) {
        this.isTouchMoving = true
      }
    },

    onTouchEnd(e, question) {
      if (!this.isTouchMoving) return
      const touch = e.changedTouches[0]
      const deltaX = touch.clientX - this.touchStartX
      // 从左边缘起势的右滑交由系统返回，不处理
      if (this.ignoreEdgeSwipe && deltaX > 0) {
        this.isTouchMoving = false
        return
      }
      // 左滑展开，右滑收起
      if (deltaX < -40) {
        this.hideAllSwipeActions()
        this.$set(question, '_showSwipeActions', true)
      } else if (deltaX > 40) {
        this.$set(question, '_showSwipeActions', false)
      }
      this.isTouchMoving = false
    },
    
    // 显示/隐藏所有卡片的滑动操作
    hideAllSwipeActions() {
      this.questionList.forEach(q => {
        if (q._showSwipeActions) this.$set(q, '_showSwipeActions', false)
      })
    },
    
    // 处理题目订正
    async handleQuestionFix(question) {
      try {
        await this.mobileConfirm('确认将该题目订正分数改为100分吗？')
        
        this.actionLoading = true
        const res = await this.$http.put(`/api/game/question/${question.question_code}/fix`)
        
        // 检查响应结构，兼容不同的响应格式
        const responseData = res.data || res
        const isSuccess = responseData.code === 200 || responseData.status === 200 || responseData.success === true
        
        if (isSuccess) {
          this.$message.success('订正成功')
          // 更新本地状态
          question.question_score_fix = 100
          // 隐藏操作按钮
          this.$set(question, '_showSwipeActions', false)
          // 刷新题目列表
          await this.fetchData()
        } else {
          throw new Error(responseData.message || responseData.msg || '订正失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('订正失败:', error)
          this.$message.error(error.message || '订正失败')
        }
      } finally {
        this.actionLoading = false
      }
    },
    
    // 处理题目收藏
    async handleQuestionCollect(question) {
      try {
        await this.mobileConfirm(
          question.is_collect ? '确认取消收藏该题目吗？' : '确认收藏该题目吗？'
        )

        this.actionLoading = true
        const res = await this.$http.put(`/api/question/${question.question_code}/collect`, {
          is_collect: !question.is_collect
        })
        
        // 检查响应结构，兼容不同的响应格式
        const responseData = res.data || res
        const isSuccess = responseData.code === 200 || responseData.status === 200 || responseData.success === true
        
        if (isSuccess) {
          // 更新本地状态
          question.is_collect = !question.is_collect
          this.$message.success(question.is_collect ? '收藏成功' : '取消收藏成功')
          // 隐藏操作按钮
          this.$set(question, '_showSwipeActions', false)
        } else {
          throw new Error(responseData.message || responseData.msg || '操作失败')
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('收藏操作失败:', error)
          this.$message.error(error.message || '操作失败')
        }
      } finally {
        this.actionLoading = false
      }
    },
    
    // 处理题目挂起
    async handleQuestionPending(question) {
      try {
        // 判断题目得分和订正分数
        if (question.question_score === 100 || question.question_score_fix === 100) {
          this.$message.warning('该题目你已经' + (question.question_score === 100 ? 'AC' : '订正') + '，无法挂起。')
          return
        }

        await this.mobileConfirm(
          question.is_pending ? '确认取消挂起该题目吗？' : '确认挂起该题目吗？'
        )
        
        this.actionLoading = true
        const res = await this.$http.put(`/api/question/${question.question_code}/pending`, {
          is_pending: !question.is_pending
        })
        
        // 检查响应结构，兼容不同的响应格式
        const responseData = res.data || res
        const isSuccess = responseData.code === 200 || responseData.status === 200 || responseData.success === true
        
        if (isSuccess) {
          // 更新本地状态
          question.is_pending = !question.is_pending
          this.$message.success(question.is_pending ? '挂起成功' : '取消挂起成功')
          // 隐藏操作按钮
          this.$set(question, '_showSwipeActions', false)
        } else {
          // 即使响应不成功，如果操作实际上成功了，我们也更新本地状态
          // 这种情况可能是后端返回格式问题，但操作实际成功
          question.is_pending = !question.is_pending
          this.$message.success(question.is_pending ? '挂起成功' : '取消挂起成功')
          // 隐藏操作按钮
          this.$set(question, '_showSwipeActions', false)
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('挂起操作失败:', error)
          // 即使网络错误，如果操作实际上成功了，我们也更新本地状态
          // 这种情况可能是网络问题，但操作实际成功
          question.is_pending = !question.is_pending
          this.$message.success(question.is_pending ? '挂起成功' : '取消挂起成功')
          // 隐藏操作按钮
          this.$set(question, '_showSwipeActions', false)
        }
      } finally {
        this.actionLoading = false
      }
    },

    // 处理题目打开链接
    async handleQuestionUrl(question) {
      if (!question.question_url) {
        this.$message.warning('该题目没有链接信息')
        return
      }

      try {
        // 直接在新标签页中打开链接
        window.open(question.question_url, '_blank')
        this.$message.success('已打开链接')
      } catch (error) {
        console.error('打开链接失败:', error)
        this.$message.error('打开链接失败')
      }
    },
    
    // 切换标签显示状态
    toggleTagsDisplay(question) {
      this.$set(question, '_showAllTags', !question._showAllTags)
    },
    // 错误类型相关
    normalizeErrors(errors) {
      if (!errors) return []
      if (Array.isArray(errors)) return errors
      try {
        const arr = JSON.parse(errors)
        return Array.isArray(arr) ? arr : []
      } catch (e) { return [] }
    },
    toggleErrorsDisplay(question) {
      this.$set(question, '_showAllErrors', !question._showAllErrors)
    },
    
    // 移动端确认对话框
    mobileConfirm(message) {
      return new Promise((resolve, reject) => {
        this.$confirm(message, '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning',
          customClass: 'mobile-confirm-dialog',
          center: true,
          closeOnClickModal: true,
        }).then(() => resolve()).catch((err) => reject(err || 'cancel'))
      })
    },
    // 获取错误类型选项
    async fetchErrorCodes() {
      try {
        const res = await getErrorCodes()
        if (res && (res.code === 200 || res.status === 200)) {
          this.errorCodes = res.data || []
        }
      } catch (e) {
        console.error('获取错误类型失败:', e)
      }
    },
    getErrorTagInlineStyle,
    getErrorOutlineStyle
  }
}
</script>

<style lang="scss" scoped>
.question-mobile-wrapper {
  height: 100vh;
  width: 100%;
  position: relative;
  overflow: hidden;
  
  .question-mobile-container {
    height: 100vh;
    width: 100%;
    display: flex;
    flex-direction: column;
    background-color: #f0f2f5;
    position: relative;
    overflow: hidden;
  }
}

/* 标题栏 */
.mobile-header {
  height: 50px;
  background-color: #304156; /* 与App.vue中的.mobile-drawer背景色保持一致 */
  background-image: linear-gradient(to bottom, rgba(255, 255, 255, 0.05), transparent); /* 添加与抽屉菜单相同的渐变效果 */
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 10;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08); /* 添加与抽屉菜单相同的底部边框 */
  transition: all 0.3s ease;
  
  .page-title {
    font-size: 18px;
    font-weight: 600;
    color: #ffffff;
    margin: 0;
  }
  
  /* 隐藏状态 */
  &.header-hidden {
    transform: translateY(-100%);
    opacity: 0;
  }
}

/* 卡片容器 */
.question-cards-container {
  flex: 1;
  overflow-y: auto;
  padding: 10px;
  padding-bottom: 20px;
  padding-top: 60px; /* 为固定定位的头部预留空间 */
  box-sizing: border-box;
  scrollbar-width: none;
  -ms-overflow-style: none;
  touch-action: pan-y; /* 允许垂直滚动，减少横向手势干扰 */
  transition: padding-top 0.3s ease; /* 添加顶部内边距的过渡动画 */
  
  &::-webkit-scrollbar {
    display: none;
  }
  
  /* 当头部隐藏时，减少顶部内边距 */
  &.header-hidden {
    padding-top: 10px;
  }
  
  .question-card {
    background-color: #ffffff;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.05);
    padding: 0;
    margin-bottom: 12px;
    position: relative;
    overflow: hidden;
    transition: all 0.2s ease;
    position: relative;
    border: 1px solid #ebeef5;
    
    &:active {
      transform: scale(0.98);
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
    }
    
    &:last-child {
      margin-bottom: 20px; /* 最后一张卡片底部增加间距 */
    }
    
    // 难度指示器（右上角三角形）
    .difficulty-indicator {
      position: absolute;
      top: 0;
      right: 0;
      width: 60px;
      height: 60px;
      overflow: hidden;
      z-index: 1;
      pointer-events: none; /* 确保点击事件透过指示器 */
      
      .triangle-background {
        position: absolute;
        top: 0;
        right: 0;
        width: 0;
        height: 0;
        border-style: solid;
        border-width: 0 60px 60px 0;
        opacity: 0.85; /* 稍微降低不透明度，使其更和谐 */
      }
      
      .difficulty-value {
        position: absolute;
        /* 调整位置确保完全居中显示 */
        top: 15px;
        right: 8px;
        /* 使文本平行于斜边 */
        transform: rotate(45deg);
        color: white;
        font-size: 12px;
        font-weight: bold;
        text-align: center;
        white-space: nowrap;
        width: 30px; /* 调整宽度确保文本居中 */
        line-height: 1;
        text-shadow: 0 1px 1px rgba(0,0,0,0.3);
      }
    }
    
    // AC/订正印章
    .fix-stamp {
      position: absolute;
      top: 45px;
      right: 10%;
      transform: translateY(-50%) rotate(-15deg);
      z-index: 5;
      pointer-events: none; /* 确保印章不影响点击 */
      
      .stamp-content {
        width: 50px;
        height: 50px;
        border-radius: 50%;
        background-color: rgba(103, 194, 58, 0.6); // 绿色 - 订正，增加透明度
        color: white;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 24px;
        font-weight: bold;
        border: 2px solid rgba(255, 255, 255, 0.6);
        box-shadow: 0 0 8px rgba(0, 0, 0, 0.15);
        text-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
        
        &.ac-stamp {
          background-color: rgba(247, 186, 42, 0.6); // 黄色 - AC，增加透明度
        }
      }
    }
    
    .question-card-main {
      padding: 10px 15px; /* 上下10px，左右15px */
      flex: 1;
      width: 100%;
      box-sizing: border-box; /* 防止padding让内容宽度溢出卡片 */
      
      .question-header {
        display: flex;
        align-items: flex-start;
        margin-bottom: 0px;
        padding-right: 15px; /* 为右上角三角形留出更多空间 */
        min-height: 26px; /* 单行高度 */
        
        .question-name {
          font-size: 15px;
          font-weight: 600;
          color: #303133;
          line-height: 1.4;
          word-break: break-word;
          margin: 0;
          padding: 0;
          flex: 1;
          display: -webkit-box;
          -webkit-line-clamp: 1; /* 仅显示一行 */
          -webkit-box-orient: vertical;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap; /* 超出隐藏 */
        }
        
        .replay-icon {
          margin-left: 0;
          margin-right: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          
          i {
            font-size: 18px;
            color: #409EFF;
            background-color: rgba(64, 158, 255, 0.1);
            border-radius: 50%;
            padding: 4px;
            transition: all 0.3s;
          }
        }
      }
      
      .question-info {
        margin-top: 3px;
        border-top: 1px solid #f5f5f5;
        padding-top: 8px;
        
        .question-game {
          font-size: 12px;
          color: #909399;
          margin-bottom: 8px;
          display: flex;
          align-items: center;

          i.el-icon-trophy {
            margin-right: 5px;
            font-size: 12px;
          }

          .game-name-text {
            flex: 1;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }
        
        .question-scores-row {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          grid-gap: 12px; /* 与赛事页一致的列间距 */
          margin-bottom: 6px;
          
          .question-score,
          .question-fix-score {
            font-size: 12px;
            display: flex;
            align-items: center;
            background-color: rgba(249, 250, 252, 0.8);
            border-radius: 6px;
            padding: 6px 10px;
            border: 1px solid #f0f2f5;
            box-sizing: border-box;
            min-width: 0;
            min-height: 28px;
            line-height: 16px;
          
            .score-value-container {
              display: flex;
              align-items: center;
              gap: 3px;
              flex: 1;
              justify-content: flex-start;
              min-width: 0;
            }
          
            .score-label {
              color: #606266;
              margin-right: 5px;
              font-weight: 500;
              min-width: 40px;
              text-align: right;
            }
            
            .score-value {
              font-size: 12px;
              font-weight: 600;
              min-width: 0;
              white-space: nowrap;
              overflow: hidden;
              text-overflow: ellipsis;
            }
          }
        }
        
        .question-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          margin-top: 4px;
          
          .el-tag {
            margin-bottom: 4px;
            font-size: 11px;
            height: 20px;
            line-height: 18px;
            padding: 0 6px;
            border-radius: 4px;
          }
          
          .more-tags-btn {
            font-size: 11px;
            color: #409EFF;
            height: 20px;
            line-height: 18px;
            padding: 0 6px;
            border: 1px solid #409EFF;
            border-radius: 4px;
            background-color: transparent;
            margin-bottom: 4px;
            transition: all 0.2s ease;
            cursor: pointer;
            user-select: none;
            -webkit-tap-highlight-color: transparent;
            
            /* 桌面端悬停效果 */
            @media (hover: hover) and (pointer: fine) {
              &:hover {
                background-color: #409EFF;
                color: white;
              }
            }
            
            /* 触摸设备的活动状态 */
            &:active {
              background-color: #409EFF;
              color: white;
              transform: scale(0.95);
            }
            
            /* 触摸设备触摸时的状态 */
            &:focus {
              outline: none;
            }
            
            /* 触摸设备触摸后的状态 */
            &:focus:not(:active) {
              background-color: transparent;
              color: #409EFF;
            }
          }
        }
      }

        /* 错误类型标签区域，样式与标签类似但和上方间距更小 */
        .question-errors {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          margin-top: 8px;
        }
    }
    
    /* 滑动显示的操作按钮覆盖层 */
    .swipe-actions {
      position: absolute;
      bottom: 0;
      right: -200px; /* 初始隐藏，增加宽度 */
      width: 200px; /* 增加容器宽度，确保背景能覆盖所有按钮 */
      height: 60px; /* 固定高度 */
      background: linear-gradient(135deg, 
        rgba(255, 255, 255, 0.95) 0%, 
        rgba(248, 249, 250, 0.95) 50%, 
        rgba(240, 242, 245, 0.95) 100%);
      backdrop-filter: blur(8px);
      border: 1px solid rgba(235, 238, 245, 0.8);
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08), 
                  0 2px 4px rgba(0, 0, 0, 0.04);
      display: flex;
      align-items: center;
      justify-content: center;
      transition: right 0.3s ease;
      z-index: 3;
      border-radius: 12px 0 0 0;
      
      &.show {
        right: 0;
      }
      
      .action-buttons {
        display: flex;
        gap: 12px; /* 增加按钮之间的间距 */
        padding-left: 15px; /* 添加左侧内边距，确保第一个按钮有足够的左间距 */
        padding-right: 15px; /* 添加右侧内边距，确保最后一个按钮有足够的右间距 */
        
        .action-icon-btn {
          width: 36px !important;
          height: 36px !important;
          min-width: 36px !important;
          padding: 0 !important;
          border-radius: 8px !important;
          background-color: #F2F3F5 !important;
          border: none !important;
          display: inline-flex !important;
          align-items: center !important;
          justify-content: center !important;
          margin: 0 !important;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
          transition: all 0.2s ease;
        }
        .action-icon-btn i { font-size: 18px !important; }
        .action-icon-btn:hover { 
          background-color: #E5E6EB !important; 
          transform: translateY(-1px);
          box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);
        }
        .action-icon-btn:active {
          transform: translateY(0);
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.06);
        }
        .action-icon-btn:disabled { background-color: #F2F3F5 !important; opacity: 1 !important; }
        .action-icon-btn:disabled i { color: #C0C4CC !important; }
        .action-icon-btn i.el-icon-check { color: #409EFF; }
        .action-icon-btn i.el-icon-star-off { color: #409EFF; }
        .action-icon-btn i.el-icon-star-on { color: #F7BA2A; }
        .action-icon-btn i.el-icon-link { color: #409EFF; }
      }
    }
  }
  
  .no-data {
    display: flex;
    justify-content: center;
    align-items: center;
    height: 100%;
    
    .no-data-content {
      text-align: center;
      color: #909399;
      
      i {
        font-size: 48px;
        margin-bottom: 16px;
      }
      
      .no-data-title {
        font-size: 18px;
        font-weight: 500;
        margin-bottom: 8px;
      }
      
      .no-data-tips {
        font-size: 14px;
        margin: 0;
      }
    }
  }
  
  .load-more {
    text-align: center;
    padding: 16px 0;
  }
  
  .loading-more {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 16px 0;
    
    i {
      margin-right: 8px;
    }
  }
  
  .no-more {
    text-align: center;
    color: #909399;
    padding: 16px 0;
    font-size: 14px;
    
    .copyright-info {
      margin-top: 12px;
      
      .mobile-copyright {
        font-size: 12px;
        color: #c0c4cc;
      }
    }
  }
}

/* 移动端操作按钮 - 与App.vue中全局菜单按钮样式保持一致 */
.mobile-action-buttons {
  position: fixed;
  right: 15px;
  bottom: 135px; /* 距离底部的距离 */
  display: flex;
  flex-direction: column;
  gap: 15px; /* 按钮之间的垂直间距 */
  z-index: 2001;
  width: 40px; /* 限制容器宽度与按钮一致 */
  align-items: center; /* 确保按钮水平居中对齐 */
  padding: 0 !important;
  margin: 0 !important;
  
  /* 确保每个按钮容器没有额外的边距和内边距 */
  .el-button {
    margin: 0 !important;
    padding: 0 !important;
    box-sizing: border-box !important;
  }
}

.mobile-icon-btn {
  width: 40px !important; /* 与App.vue保持一致 */
  height: 40px !important; /* 与App.vue保持一致 */
  border-radius: 40px !important; /* 与App.vue保持一致 */
  background-color: rgba(255, 255, 255, 0.5) !important; /* 与App.vue保持一致 */
  color: #409eff !important;
  border: none !important;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1) !important;
  display: flex !important;
  justify-content: center !important;
  align-items: center !important;
  padding: 0 !important;
  margin: 0 !important; /* 确保没有外边距干扰对齐 */
  transition: all 0.3s ease !important;
  position: relative !important; /* 使用相对定位 */
  left: 0 !important; /* 确保没有左偏移 */
  
  i {
    font-size: 20px !important; /* 与App.vue保持一致 */
    margin: 0 !important; /* 确保图标没有外边距 */
    padding: 0 !important; /* 确保图标没有内边距 */
    display: inline-flex !important; /* 使用inline-flex布局 */
    justify-content: center !important; /* 水平居中 */
    align-items: center !important; /* 垂直居中 */
    width: 20px !important; /* 固定宽度 */
    height: 20px !important; /* 固定高度 */
  }
  
  &:hover, &:focus {
    background-color: rgba(255, 255, 255, 0.8) !important; /* 与App.vue保持一致 */
    transform: scale(1.05) !important;
  }
  
  &:active {
    background-color: rgba(255, 255, 255, 0.9) !important; /* 与App.vue保持一致 */
    transform: scale(0.95) !important;
  }
}

// 自定义弹窗样式
.dialog-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 2000;
  
  .dialog-mask {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.5);
  }
  
  .custom-dialog {
    position: absolute;
    left: 5%;
    width: 90%;
    max-height: 80vh;
    background-color: #ffffff;
    border-radius: 8px;
    box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
    overflow: hidden;
    
    .dialog-close {
      position: absolute;
      top: 8px;
      right: 12px;
      font-size: 24px;
      color: #909399;
      cursor: pointer;
      z-index: 10;
    }
    
    .dialog-body {
      max-height: 80vh;
      overflow-y: auto;
      -webkit-overflow-scrolling: touch;
      
      .dialog-content {
        padding: 5px;
        
        .dialog-custom-title {
          font-size: 18px;
          font-weight: 600;
          color: #303133;
          margin-top: 0;
          margin-bottom: 16px;
          padding-bottom: 10px;
          border-bottom: 1px solid #ebeef5;
        }
        
        &.filter-dialog-content {
          padding: 0;
          
          .filter-title {
            font-size: 16px;
            margin: 0 0 10px 0;
            padding-bottom: 8px;
          }
        }
      }
      
      &.statistics-dialog-body {
        max-height: none;
        overflow-y: visible;
        padding: 12px 10px;
        
        .mobile-statistics-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 8px;
          padding: 2px 0;
          max-height: none;
          overflow-y: visible;
          
          .stat-item {
            background-color: #f9fafc;
            border-radius: 8px;
            padding: 8px 5px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
            transition: all 0.3s ease;
            position: relative;
            overflow: hidden;
            min-height: 65px;
            height: auto;
            
            &:active {
              transform: scale(0.98);
            }
            
            /* 给不同卡片添加渐变背景色 */
            &:nth-child(1) {
              background: linear-gradient(to top, 
                rgba(226, 206, 186, 0.3) 0%, 
                rgba(226, 206, 186, 1) 100%);
              .stat-label, i { color: #8D7B64; }
              .stat-value { color: #6D5B44; }
            }
            &:nth-child(2) {
              background: linear-gradient(to top, 
                rgba(209, 178, 164, 0.3) 0%, 
                rgba(209, 178, 164, 1) 100%);
              .stat-label, i { color: #956B5A; }
              .stat-value { color: #754B3A; }
            }
            &:nth-child(3) {
              background: linear-gradient(to top, 
                rgba(190, 206, 185, 0.3) 0%, 
                rgba(190, 206, 185, 1) 100%);
              .stat-label, i { color: #5B7155; }
              .stat-value { color: #3B5135; }
            }
            &:nth-child(4) {
              background: linear-gradient(to top, 
                rgba(199, 188, 205, 0.3) 0%, 
                rgba(199, 188, 205, 1) 100%);
              .stat-label, i { color: #6B5873; }
              .stat-value { color: #4B3853; }
            }
            &:nth-child(5) {
              background: linear-gradient(to top, 
                rgba(187, 199, 214, 0.3) 0%, 
                rgba(187, 199, 214, 1) 100%);
              .stat-label, i { color: #4A5F78; }
              .stat-value { color: #2A3F58; }
            }
            &:nth-child(6) {
              background: linear-gradient(to top, 
                rgba(220, 198, 170, 0.3) 0%, 
                rgba(220, 198, 170, 1) 100%);
              .stat-label, i { color: #8B7447; }
              .stat-value { color: #6B5427; }
            }
            
            .stat-value {
              font-size: 16px;
              font-weight: 600;
              margin-bottom: 5px;
              text-align: center;
              line-height: 1.2;
            }
            
            .stat-label {
              font-size: 11px;
              display: flex;
              align-items: center;
              justify-content: center;
              
              i {
                margin-right: 3px;
                font-size: 13px;
              }
            }
          }
        }
      }
      
      &.filter-dialog-body {
        padding: 15px 15px 10px;
        
        .filter-form {
          .el-form-item {
            margin-bottom: 10px;
            
            .el-form-item__label {
              padding-right: 5px;
              line-height: 32px;
            }
            
            .el-form-item__content {
              line-height: 32px;
            }
          }
          
          ::v-deep .el-input__inner,
          ::v-deep .el-select .el-input__inner {
            height: 32px;
            line-height: 32px;
          }
        }
        
        .filter-footer {
          margin-top: 10px;
          padding-top: 8px;
          border-top: 1px solid #ebeef5;
          text-align: right;
          
          .el-button {
            padding: 7px 15px;
            margin-left: 8px;
          }
        }
      }
      
      &.replay-dialog-body {
        padding: 15px 15px 10px;
        max-height: 70vh;
        min-height: 300px;
        overflow-y: auto;
        scrollbar-width: none;
        -ms-overflow-style: none;
        
        &::-webkit-scrollbar {
          display: none;
        }
        
        .replay-dialog-content {
          padding: 15px;
          height: 100%;
          display: flex;
          flex-direction: column;
          
          .dialog-custom-title {
            margin: 0 0 15px 0;
            padding-bottom: 10px;
            border-bottom: 1px solid #ebeef5;
            flex-shrink: 0;
          }
          
          .replay-content {
            flex: 1;
            overflow-y: auto;
            min-height: 0;
            scrollbar-width: none;
            -ms-overflow-style: none;
            
            &::-webkit-scrollbar {
              display: none;
            }
            
            .md-editor {
              border: none;
              height: 100%;
              
              :deep(.v-note-wrapper) {
                min-height: auto !important;
                height: 100% !important;
                z-index: 1;
              }
              
              :deep(.v-note-panel) {
                border: none;
                height: 100%;
              }
              
              :deep(.v-note-show) {
                height: 100%;
                overflow-y: auto;
                scrollbar-width: none;
                -ms-overflow-style: none;
                
                &::-webkit-scrollbar {
                  display: none;
                }
              }
            }
          }
          
          .empty-text {
            text-align: center;
            color: #909399;
            padding: 20px 0;
            flex: 1;
            display: flex;
            align-items: center;
            justify-content: center;
          }
        }
      }
    }
  }

  /* 统计弹窗：与PlanMobile一致的尺寸与padding */
  .custom-dialog.stats-dialog {
    left: 4%;
    width: 92%;
    max-width: 520px;
    max-height: none;
  }
  .custom-dialog.stats-dialog .dialog-body.statistics-dialog-body {
    max-height: none;
    overflow-y: visible;
    padding: 12px 10px;
  }
}
</style>

<!-- 全局样式：专门用于覆盖Element UI弹窗 -->
<style>
/* 移动端确认弹窗优化样式 */
.mobile-confirm-dialog {
  width: 88% !important;
  max-width: 420px !important;
  border-radius: 12px !important;
}
.mobile-confirm-dialog .el-message-box__header {
  padding: 12px 16px !important;
}
.mobile-confirm-dialog .el-message-box__title {
  font-size: 18px !important;
}
.mobile-confirm-dialog .el-message-box__content {
  padding: 12px 16px 0 16px !important;
  font-size: 15px !important;
  line-height: 1.6 !important;
}
.mobile-confirm-dialog .el-message-box__btns {
  padding: 12px 16px 16px !important;
}
.mobile-confirm-dialog .el-button + .el-button {
  margin-left: 12px !important;
}
.mobile-confirm-dialog .el-message-box__status { display: none !important; }
.mobile-confirm-dialog .el-button { padding: 10px 14px !important; }

/* 触摸设备优化样式 */
@media (hover: none) and (pointer: coarse) {
  /* 触摸设备上禁用悬停效果 */
  .more-tags-btn:hover {
    background-color: transparent !important;
    color: #409EFF !important;
  }
  
  /* 触摸设备上的触摸反馈 */
  .more-tags-btn:active {
    background-color: #409EFF !important;
    color: white !important;
    transform: scale(0.95) !important;
  }
  
  /* 触摸设备触摸后的状态重置 */
  .more-tags-btn:focus:not(:active) {
    background-color: transparent !important;
    color: #409EFF !important;
    transform: none !important;
  }
}
</style>